/**
 * Central utility to clear all stale data from localStorage
 * This includes connection, chat, auth, and other cached data
 */

// Configuration for stale data thresholds
const STALE_THRESHOLDS = {
  connection: 60 * 1000,        // 1 minute for connection status
  chat: 24 * 60 * 60 * 1000,    // 24 hours for chat sessions
  auth: 7 * 24 * 60 * 60 * 1000, // 7 days for auth tokens
  cache: 5 * 60 * 1000           // 5 minutes for general cache
};

// Flag to prevent multiple clearing in React Strict Mode
let hasCleared = false;

/**
 * Clear all stale data - main entry point
 */
export function clearAllStaleData(): void {
  console.log('🧹 === Starting comprehensive stale data cleanup ===');
  
  clearStaleConnectionData();
  clearStaleChatData();
  clearStaleAuthData();
  clearStaleCacheData();
  
  console.log('✅ === Stale data cleanup completed ===\n');
}

/**
 * Clear stale connection data
 */
export function clearStaleConnectionData(): void {
  console.log('🔌 Clearing stale connection data...');
  
  const connectionStorageKey = 'connection-storage';
  const storedData = localStorage.getItem(connectionStorageKey);
  
  if (storedData) {
    try {
      const parsed = JSON.parse(storedData);
      const state = parsed.state;
      
      if (state) {
        const lastChecked = state.lastChecked ? new Date(state.lastChecked).getTime() : 0;
        const now = Date.now();
        const isStale = (now - lastChecked) > STALE_THRESHOLDS.connection;
        
        // ALWAYS clear if status is connected OR if there are companies (since backend says not_registered)
        // or if data is stale
        if (state.status === 'connected' || isStale || (state.companies && state.companies.length > 0)) {
          console.warn('  ⚠️ Found connection data that needs clearing:');
          console.log('    - Previous status:', state.status);
          console.log('    - Previous companies:', state.companies?.length || 0);
          if (state.companies && state.companies.length > 0) {
            console.log('    - Company data:', state.companies);
          }
          console.log('    - Last checked:', lastChecked ? new Date(lastChecked).toISOString() : 'never');
          console.log('    - Age:', Math.floor((now - lastChecked) / 1000), 'seconds');
          
          // Reset to clean state - COMPLETELY CLEAR everything
          parsed.state = {
            status: 'not_connected',
            lastConnected: null,
            deviceName: null,
            tallyVersion: null,
            companyName: null,
            connectionId: null,
            companies: [], // Clear companies array
            selectedCompany: null,
            lastChecked: null,
            consecutiveFailures: 0,
            isHealthy: false,
            isLoading: false,
            error: null,
            isInitialized: false,
            _healthMonitorInterval: null
          };
          
          // Force save the cleared state
          localStorage.setItem(connectionStorageKey, JSON.stringify(parsed));
          
          // Verify it was saved
          const verification = localStorage.getItem(connectionStorageKey);
          if (verification) {
            const verifyParsed = JSON.parse(verification);
            console.log('  ✅ Verified cleared state:', {
              status: verifyParsed.state?.status,
              companies: verifyParsed.state?.companies?.length || 0
            });
          }
          console.log('  ✅ Connection state reset to not_connected with empty companies');
        } else {
          console.log('  ℹ️ Connection data is fresh and not connected');
        }
      }
    } catch (e) {
      console.error('  ❌ Failed to parse connection storage, removing:', e);
      localStorage.removeItem(connectionStorageKey);
    }
  }
  
  // Clear related connection keys
  const connectionKeys = [
    'connectionCache',
    'dashboardInitialized',
    'selectedTallyCompany'
  ];
  
  connectionKeys.forEach(key => {
    if (localStorage.getItem(key)) {
      console.log(`  🗑️ Removing ${key}`);
      localStorage.removeItem(key);
    }
  });
}

/**
 * Clear stale chat data
 */
export function clearStaleChatData(): void {
  console.log('💬 Clearing stale chat data...');
  
  const chatKeys = [
    'chat_session_id',
    'current_chat_session',
    'chat-session-storage',
    'chat-storage',
    'chat_session_timestamp'
  ];
  
  chatKeys.forEach(key => {
    if (localStorage.getItem(key)) {
      console.log(`  🗑️ Removing ${key}`);
      localStorage.removeItem(key);
    }
  });
  
  // Clear UUID-like keys (old session IDs)
  const allKeys = Object.keys(localStorage);
  allKeys.forEach(key => {
    if (key.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      console.log(`  🗑️ Removing old session: ${key}`);
      localStorage.removeItem(key);
    }
    
    if (key.startsWith('chat_') && !chatKeys.includes(key)) {
      console.log(`  🗑️ Removing chat key: ${key}`);
      localStorage.removeItem(key);
    }
  });
}

/**
 * Clear stale auth data
 */
export function clearStaleAuthData(): void {
  console.log('🔐 Clearing stale auth data...');
  
  const authToken = localStorage.getItem('auth_token');
  const authTimestamp = localStorage.getItem('auth_timestamp');
  
  if (authToken && authTimestamp) {
    const age = Date.now() - parseInt(authTimestamp);
    if (age > STALE_THRESHOLDS.auth) {
      console.log('  ⚠️ Auth token is stale (', Math.floor(age / (24 * 60 * 60 * 1000)), 'days old)');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_timestamp');
      localStorage.removeItem('auth-storage');
      console.log('  ✅ Auth data cleared');
    } else {
      console.log('  ℹ️ Auth token is fresh');
    }
  }
}

/**
 * Clear stale cache data
 */
export function clearStaleCacheData(): void {
  console.log('📦 Clearing stale cache data...');
  
  const cacheKeys = [
    'api_cache',
    'company_cache',
    'report_cache',
    'dashboard_cache'
  ];
  
  cacheKeys.forEach(key => {
    const data = localStorage.getItem(key);
    if (data) {
      try {
        const parsed = JSON.parse(data);
        if (parsed.timestamp) {
          const age = Date.now() - parsed.timestamp;
          if (age > STALE_THRESHOLDS.cache) {
            console.log(`  🗑️ Removing stale cache: ${key} (${Math.floor(age / 1000)}s old)`);
            localStorage.removeItem(key);
          }
        }
      } catch {
        // Invalid cache data, remove it
        console.log(`  🗑️ Removing invalid cache: ${key}`);
        localStorage.removeItem(key);
      }
    }
  });
}

/**
 * Auto-clear on page load if needed
 */
export function checkAndClearIfNeeded(): void {
  // Prevent multiple clears during React Strict Mode double-mounting
  if (hasCleared) {
    console.log('🔍 Stale data already cleared, skipping...');
    return;
  }
  
  console.log('🔍 Checking for stale data on page load...');
  hasCleared = true;
  
  // Always clear stale connection data on page load to prevent 
  // showing incorrect "connected" status from previous sessions
  clearStaleConnectionData();
  
  // Check chat sessions
  const storedSessionId = localStorage.getItem('chat_session_id');
  if (storedSessionId) {
    const sessionAge = localStorage.getItem('chat_session_timestamp');
    if (sessionAge) {
      const age = Date.now() - parseInt(sessionAge);
      if (age > STALE_THRESHOLDS.chat) {
        console.log('  ⚠️ Chat session is too old, clearing...');
        clearStaleChatData();
      }
    } else {
      console.log('  ⚠️ Old chat session format detected, clearing...');
      clearStaleChatData();
    }
  }
  
  // Clean other stale data
  clearStaleAuthData();
  clearStaleCacheData();
}